import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { ListingPhoto } from "@/components/PhotoPlaceholder";
import BestOfferBadge from "@/components/BestOfferBadge";
import ListingBadges from "@/components/ListingBadges";
import EditListingModal from "@/components/EditListingModal";
import { subscribeListingUpdates, mergeListingInto } from "@/lib/listingBus";
import { formatGBP, conditionLabel } from "@/lib/marketplace";
import { ArrowLeft, Star, PoundSterling, Box, Clock, CheckCircle2, Link2, Check, Wallet, ArrowDownLeft, ArrowUpRight, Loader2, Unlock, Pencil } from "lucide-react";
import KidActionsToolbar, { KidHandleBadge } from "@/components/KidActionsToolbar";
import ChildLedger from "@/components/ChildLedger";

// Iteration 294 — Org "Legal & Compliance" card: signed status for the
// Commercial Participation Agreement + view/download access.
function PartnerAgreementCard({ kidId }) {
  const [sig, setSig] = useState(null);
  useEffect(() => {
    api.get(`/kids/${kidId}/partner-agreement`)
      .then(({ data }) => setSig(data))
      .catch(() => setSig(null));
  }, [kidId]);
  return (
    <div className="card-base p-6 mt-6" data-testid="partner-legal-compliance">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-brandSlate">Legal &amp; Compliance</div>
          <p className="text-sm text-slate-600 mt-1">Commercial Participation Agreement with Digitally Prepared Ltd (t/a MyTableTopApp).</p>
        </div>
        {sig?.signed ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="partner-agreement-status">
            <CheckCircle2 className="w-3.5 h-3.5" /> Signed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200" data-testid="partner-agreement-status">
            Not signed
          </span>
        )}
      </div>
      {sig?.signed && (
        <div className="mt-3 text-xs text-slate-500" data-testid="partner-agreement-meta">
          Signed {(sig.agreed_at || "").slice(0, 10)} · Signatory role: {sig.signatory_role} · Agreement v{sig.agreement_version}
        </div>
      )}
      <div className="mt-4 flex gap-2 flex-wrap">
        <Link
          to="/legal/partner-agreement"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brandTeal text-white text-xs font-bold hover:bg-brandTealDark transition"
          data-testid="partner-agreement-view-btn"
        >
          View full agreement (6 sections)
        </Link>
        <Link
          to="/legal/partner-agreement"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold text-slate-600 hover:border-brandTeal hover:text-brandTeal transition"
          data-testid="partner-agreement-download-btn"
        >
          Download
        </Link>
      </div>
    </div>
  );
}

// Iteration 289 — Org cause dashboard: Total Raised vs Active Pledged
// summary cards + Direct Sales & Orders / Supporter Listings tabs,
// fed by GET /causes/{id}/overview.
function CauseDashboard({ kidId }) {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("direct");
  useEffect(() => {
    let cancel = false;
    api.get(`/causes/${kidId}/overview`)
      .then(({ data }) => { if (!cancel) setOverview(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [kidId]);
  if (!overview) return null;
  const direct = overview.direct_listings || [];
  const supporters = overview.supporter_listings || [];
  const splits = overview.splits || [];
  return (
    <div className="card-base p-4 mt-3" data-testid="kid-cause-dashboard">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-teal-50 border border-teal-200 p-3" data-testid="kid-cause-raised">
          <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">Total Raised</div>
          <div className="font-display text-2xl font-semibold text-slate-900" data-testid="kid-cause-raised-total">{formatGBP((overview.total_raised_pennies || 0) / 100)}</div>
          <div className="text-[11px] text-slate-500">{splits.length} donation{splits.length === 1 ? "" : "s"} recorded</div>
        </div>
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3" data-testid="kid-cause-pledged">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800">Active Pledged</div>
          <div className="font-display text-2xl font-semibold text-slate-900" data-testid="kid-cause-pledged-total">{formatGBP((overview.active_pledged_pennies || 0) / 100)}</div>
          <div className="text-[11px] text-slate-500">from live supporter listings</div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-3">
        {[{ k: "direct", label: "Sales & Orders", t: "kid-cause-tab-direct" }, { k: "supporters", label: "Supporter Listings", t: "kid-cause-tab-supporters" }].map((x) => (
          <button key={x.k} type="button" onClick={() => setTab(x.k)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${tab === x.k ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={x.t}>{x.label}</button>
        ))}
      </div>
      <div className="mt-3">
        {tab === "direct" ? (
          <div className="space-y-1.5" data-testid="kid-cause-direct-list">
            {direct.length === 0 && splits.length === 0 && <div className="text-sm text-slate-500">No direct listings or sales yet.</div>}
            {direct.map((d) => (
              <div key={d.listing_id} className="flex items-center justify-between text-sm border-t border-slate-100 pt-1.5" data-testid={`kid-cause-direct-${d.listing_id}`}>
                <span className="text-slate-700 truncate mr-2">{d.title}</span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{d.status}{d.reserved ? " · reserved" : ""}</span>
                  {d.price_pennies != null && <span className="font-semibold text-slate-900">{formatGBP(d.price_pennies / 100)}</span>}
                </span>
              </div>
            ))}
            {splits.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-t border-slate-100 pt-1.5" data-testid={`kid-cause-split-${s.id}`}>
                <span className="text-slate-700 truncate mr-2">{s.listing_title || "Sale"} · {s.split_pct}% split</span>
                <span className="font-semibold text-teal-700 whitespace-nowrap">+{formatGBP((s.cause_pennies || 0) / 100)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="kid-cause-supporter-list">
            {supporters.length === 0 && <div className="text-sm text-slate-500">No supporter listings yet — household items tagged to this cause will appear here.</div>}
            {supporters.map((s) => (
              <div key={s.listing_id} className="flex items-center justify-between text-sm border-t border-slate-100 pt-1.5 gap-2" data-testid={`kid-cause-supporter-${s.listing_id}`}>
                <span className="text-slate-700 truncate">{s.title}</span>
                <span className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-[11px] text-slate-500">{s.seller_name}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${s.status === "sold" ? "text-emerald-700" : s.status === "archived" ? "text-slate-400" : "text-sky-700"}`}>{s.status}</span>
                  <span className="font-semibold text-rose-700">{s.split_pct}%</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function KidProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [kid, setKid] = useState(null);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [err, setErr] = useState("");
  const [shareCopied, setShareCopied] = useState(false);
  const [shareError, setShareError] = useState("");
  const [walletAvailablePence, setWalletAvailablePence] = useState(0);
  const [toolbarFlash, setToolbarFlash] = useState("");
  // Kid wallet ledger
  const [wallet, setWallet] = useState(null); // { wallet_pennies, transactions }
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");
  const [releasingId, setReleasingId] = useState("");
  const [releaseFlash, setReleaseFlash] = useState("");
  const [editingListing, setEditingListing] = useState(null);
  // Iteration 112 — interactive stat card filter. Tapping Pending /
  // Live / Reserved / Orders below slices the inline sections so the
  // parent can drill into this child without leaving the page.
  // `null` = show everything, else one of "pending" | "live" |
  // "reserved" | "orders".
  const [activeFilter, setActiveFilter] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const forceRelease = async (listingId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setReleasingId(listingId);
    setReleaseFlash("");
    try {
      const { data } = await api.post(`/parent/listings/${listingId}/force-release`);
      // Iteration 74 — force-release is now deterministic: the web-tier
      // override always flips the listing to available immediately.
      const upstreamNote = data?.upstream_reset_ok === false
        ? " (mobile app will sync shortly)"
        : "";
      const releasedCount = data?.released || 0;
      setReleaseFlash(
        releasedCount
          ? `Released ${releasedCount} reservation${releasedCount === 1 ? "" : "s"} — the item is back on the marketplace.${upstreamNote}`
          : `Listing reset to available — the item is back on the marketplace.${upstreamNote}`
      );
      // Refresh listings
      const { data: fresh } = await api.get("/listings/mine/all");
      setListings((fresh || []).filter((l) => l.kid_id === id));
    } catch (ex) {
      setReleaseFlash(formatApiError(ex) || "Couldn't release. Try again.");
    } finally {
      setReleasingId("");
    }
  };

  const shopUrl = typeof window !== "undefined"
    ? `${window.location.origin}/shop/${id}`
    : `/shop/${id}`;

  const handleShareShop = async () => {
    setShareError("");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shopUrl);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement("textarea");
        ta.value = shopUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute"; ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (_) {
      setShareError("Could not copy link — please copy the address bar manually.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [kidsResp, statsResp, mineResp, walletResp] = await Promise.all([
          api.get("/kids"),
          api.get(`/dashboard/kid/${id}`),
          api.get("/listings/mine/all"),
          api.get("/wallet/status").catch(() => ({ data: {} })),
        ]);
        const k = (kidsResp.data || []).find((x) => x.id === id);
        setKid(k || null);
        setStats(statsResp.data);
        setListings((mineResp.data || []).filter((l) => l.kid_id === id));
        // Household wallet — used by the Top Up modal's cap check.
        const pence = walletResp.data?.wallet_available_pennies
          ?? walletResp.data?.wallet_pennies
          ?? walletResp.data?.available_pennies
          ?? 0;
        setWalletAvailablePence(pence);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  // Kid wallet ledger — fetched separately so a wallet-history failure doesn't
  // gate the whole profile from rendering.
  useEffect(() => {
    (async () => {
      setWalletLoading(true); setWalletError("");
      try {
        const { data } = await api.get(`/kids/${id}/wallet/history?limit=25&_t=${Date.now()}`);
        setWallet(data);
      } catch (e) {
        setWalletError(formatApiError(e) || "Could not load wallet history.");
      } finally { setWalletLoading(false); }
    })();
  }, [id]);

  // Iteration 112 — this kid's orders (both seller-side and buyer-side)
  // so tapping the "Orders" stat card renders an inline order list
  // without navigating away. Dedupe by <order_group_id>::<role> so
  // upstream perspective rows for kid → kid sales collapse cleanly.
  useEffect(() => {
    let cancel = false;
    (async () => {
      setOrdersLoading(true);
      try {
        const { data } = await api.get("/orders");
        if (cancel) return;
        const all = Array.isArray(data) ? data : (data?.orders || []);
        const seen = new Set();
        const mine = [];
        for (const o of all) {
          const sellerKid = o.seller_kid_id || (o.seller || {}).kid_id;
          const buyerKid = o.buyer_kid_id || (o.buyer || {}).kid_id;
          if (sellerKid !== id && buyerKid !== id) continue;
          const gid = o.order_group_id || o.transaction_id || o.id;
          const role = o.role || (sellerKid === id ? "sold" : "bought");
          const key = `${gid}::${role}`;
          if (seen.has(key)) continue;
          seen.add(key);
          mine.push({ ...o, role });
        }
        setOrders(mine);
      } catch { if (!cancel) setOrders([]); }
      finally { if (!cancel) setOrdersLoading(false); }
    })();
    return () => { cancel = true; };
  }, [id]);

  // Cross-tab / cross-surface revalidation for listings.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setListings((prev) => mergeListingInto(prev, updated));
    });
  }, []);

  if (err) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-rose-600" data-testid="kid-profile-error">{err}</div></div>;
  if (!kid || !stats) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Loading…</div></div>;

  // Iteration 112 — status buckets used by the interactive stat cards.
  // Keep in sync with the backend `deps.py` sets AND `mappings.py`
  // `listing_from_toy` status_map so the counts match what the top
  // stats card shows.
  //
  // Iteration 146 — expanded to include every pending-flavoured slug
  // that can leak through when `_apply_listing_metadata` writes a
  // `status_override` (e.g. `pending_parent_approval` after a kid
  // edit) so the click-through count agrees with the stat card.
  const PENDING_SET = new Set([
    "pending", "pending_review", "pending_approval",
    "pending_parent", "pending_parent_review", "pending_parent_approval",
    "awaiting_approval", "awaiting_parent_review",
  ]);
  const LIVE_SET = new Set(["live", "approved", "active", "public"]);
  const filteredListings = (() => {
    const norm = (s) => (s || "").toString().toLowerCase();
    if (activeFilter === "pending") return listings.filter((l) => PENDING_SET.has(norm(l.status)));
    if (activeFilter === "live") return listings.filter((l) => LIVE_SET.has(norm(l.status)));
    if (activeFilter === "reserved") return listings.filter((l) => norm(l.status) === "reserved");
    return listings;
  })();
  const filterLabel = {
    pending: "Pending listings",
    live: "Live listings",
    reserved: "Reserved items",
    orders: "Orders",
  }[activeFilter] || `${kid.name}'s listings`;

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/dashboard/parent" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="kid-profile-back"><ArrowLeft className="w-4 h-4" /> Back to dashboard</Link>
        <div className="flex flex-wrap items-start gap-5 mt-4">
          <div className="w-16 h-16 rounded-2xl bg-blue-100 text-blue-700 text-4xl flex items-center justify-center flex-shrink-0">{kid.avatar_emoji}</div>
          <div className="flex-1 min-w-0">
            <div className="pill bg-blue-100 text-blue-800">Profile</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-1" data-testid="kid-profile-name">{kid.name}</h1>
            {kid.account_type === "organization" && kid.org_status === "verified" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800 border border-teal-200 mt-2" data-testid="kid-profile-cause-badge">
                <CheckCircle2 className="w-3.5 h-3.5" /> Verified PTA / Cause
              </span>
            )}
            {kid.account_type === "organization" && kid.org_status !== "verified" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-200 mt-2" data-testid="kid-profile-pending-badge">
                <Clock className="w-3.5 h-3.5" /> Verification Pending
              </span>
            )}
            <div className="text-slate-500 mt-1">{kid.account_type === "organization" ? "Fundraising Cause Account" : `Age ${kid.age ?? "—"}`}{stats.avg_rating != null && (<span className="ml-3 inline-flex items-center text-amber-600"><Star className="w-4 h-4 fill-amber-500 mr-1" />{stats.avg_rating.toFixed(1)}<span className="text-slate-400 ml-1">({stats.rating_count})</span></span>)}</div>
            <div className="mt-3">
              <KidHandleBadge handle={kid.handle} size="lg" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleShareShop}
              className={`inline-flex items-center gap-2 font-bold px-4 py-2.5 rounded-2xl border transition-all shadow-soft ${
                shareCopied
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-white border-brandTeal/40 text-brandTeal hover:bg-brandTeal/10"
              }`}
              data-testid="kid-share-shop-btn"
              aria-label={`Share ${kid.name}'s public shop link`}
            >
              {shareCopied ? (
                <><Check className="w-4 h-4" /> Link copied!</>
              ) : (
                <><Link2 className="w-4 h-4" /> Share Shop</>
              )}
            </button>
            {shareError && (
              <div className="text-xs text-rose-600" data-testid="kid-share-shop-error">{shareError}</div>
            )}
          </div>
        </div>

        {toolbarFlash && (
          <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-900" data-testid="kid-profile-flash" role="status">
            {toolbarFlash}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-1 min-w-0">
            <KidActionsToolbar
              kid={kid}
              walletAvailablePence={walletAvailablePence}
              onHandleUpdated={(h) => setKid((k) => ({ ...k, handle: h }))}
              onFlash={(msg) => { setToolbarFlash(msg); setTimeout(() => setToolbarFlash(""), 5000); }}
              onTopUp={() => {
                // Household wallet transfers are gated by the parent PIN modal
                // which lives on the ParentDashboard. Kick the user there with
                // a query param so the dashboard opens the transfer straight
                // away for this kid.
                navigate(`/dashboard/parent?send_to=${kid.id}`);
              }}
              onResetPin={() => {
                navigate(`/dashboard/parent?resetpin=${kid.id}`);
              }}
            />
          </div>
          <div className="lg:col-span-2 min-w-0">

        {/* Iteration 112 — interactive top stat bar. Pending / Live /
            Reserved / Orders act as tap-toggles that slice the inline
            listings + orders sections below (parents can drill into
            Tom's specific items without leaving the page). Earnings
            scrolls to the passbook. History still deep-links to the
            historical ledger tab. */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-0" data-testid="kid-stats">
          {[
            { i: <Clock className="w-5 h-5 text-amber-500" />,         t: "Pending",  v: stats.pending_approval ?? 0,                                                        k: "pending",  filter: "pending"  },
            { i: <Box className="w-5 h-5 text-emerald-500" />,         t: "Live",     v: stats.live_listings ?? 0,                                                           k: "live",     filter: "live"     },
            { i: <PoundSterling className="w-5 h-5 text-amber-600" />, t: "Reserved", v: stats.reserved_listings ?? 0,                                                       k: "reserved", filter: "reserved" },
            { i: <CheckCircle2 className="w-5 h-5 text-blue-500" />,   t: "Orders",   v: stats.orders_count ?? ((stats.sold_listings || 0) + (stats.bought_orders || 0)),   k: "orders",   filter: "orders"   },
            { i: <PoundSterling className="w-5 h-5 text-rose-500" />,  t: "Earnings", v: formatGBP(stats.earnings ?? 0),                                                     k: "earnings", filter: "earnings" },
            { i: <Box className="w-5 h-5 text-slate-500" />,           t: "History",  v: "View",                                                                             k: "history",  to: `/dashboard/ledger?tab=history&kid=${id}` },
          ].map((s) => {
            const isActive = activeFilter === s.filter;
            const activeCls = isActive ? "border-brandSlate ring-2 ring-brandSlate/30 shadow-md" : "hover:border-brandSlate hover:shadow-md";
            const inner = (
              <>
                <div className="flex items-center justify-between"><span className="text-sm font-semibold text-slate-500">{s.t}</span>{s.i}</div>
                <div className="text-2xl md:text-3xl font-display font-semibold text-slate-900 mt-2">{s.v}</div>
              </>
            );
            if (s.to) {
              return (
                <Link key={s.k} to={s.to} className={`card-base p-5 transition-all ${activeCls}`} data-testid={`kid-stat-${s.k}`}>{inner}</Link>
              );
            }
            return (
              <button
                key={s.k}
                type="button"
                onClick={() => {
                  if (s.filter === "earnings") {
                    // Scroll the wallet + passbook into view — no
                    // filter state (Earnings has no listing/order slice).
                    document.querySelector('[data-testid="kid-wallet-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                    return;
                  }
                  // Toggle — tapping the active card clears the filter.
                  setActiveFilter((cur) => (cur === s.filter ? null : s.filter));
                  // Scroll the inline list into view for immediate feedback.
                  setTimeout(() => {
                    document.querySelector('[data-testid="kid-listings"], [data-testid="kid-orders"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
                className={`card-base p-5 transition-all text-left ${activeCls}`}
                data-testid={`kid-stat-${s.k}`}
                aria-pressed={isActive}
              >{inner}</button>
            );
          })}
        </div>

        {/* Kid wallet — live balance + recent transactions */}
        <div className="card-base p-6 md:p-8 mt-10" data-testid="kid-wallet-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-2xl font-medium text-slate-900 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-600" /> {kid.name}&rsquo;s wallet
            </h2>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700">Available</div>
              <div className="text-3xl font-display font-semibold text-emerald-900 leading-none mt-0.5" data-testid="kid-wallet-balance">
                {formatGBP((wallet?.wallet_pennies ?? kid.wallet_pennies ?? 0) / 100)}
              </div>
            </div>
          </div>
          <div className="mt-5">
            {walletLoading && (
              <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="kid-wallet-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading wallet history…
              </div>
            )}
            {walletError && !walletLoading && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="kid-wallet-error">{walletError}</div>
            )}
            {!walletLoading && !walletError && (
              (wallet?.transactions || []).length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6 border border-slate-100 rounded-2xl" data-testid="kid-wallet-empty">
                  No transactions yet — earnings, spending, and parent transfers will show up here.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-100 overflow-hidden max-h-96 overflow-y-auto" data-testid="kid-wallet-list">
                  {(wallet.transactions || []).map((t, idx) => {
                    // Iteration 111 — bind directly to the new canonical
                    // fields the backend now emits on every row:
                    //   `direction` — strict "credit" / "debit"
                    //   `delta_pennies` — signed (negative for debits)
                    // A debit reads "-£3.30" in red; a credit reads
                    // "+£X.XX" in green. Falls back to the legacy
                    // amount_pennies + type inference for older upstream
                    // payloads (kept for a release cycle until upstream
                    // fully migrates).
                    const delta = Number(
                      t.delta_pennies !== undefined && t.delta_pennies !== null
                        ? t.delta_pennies
                        : (t.direction === "debit"
                            ? -Math.abs(Number(t.amount_pennies ?? t.pennies ?? 0))
                            : Number(t.amount_pennies ?? t.pennies ?? 0))
                    );
                    const isCredit = t.direction
                      ? t.direction === "credit"
                      : delta >= 0;
                    const label = t.description || t.note || t.type || "Transaction";
                    const when = t.created_at || t.timestamp || t.date;
                    const dateTxt = when ? new Date(when).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
                    return (
                      <li key={t.id || idx} className="flex items-center gap-3 px-4 py-3" data-testid={`kid-wallet-item-${idx}`}>
                        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-full shrink-0 ${isCredit ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                          {isCredit ? <ArrowDownLeft className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
                          <div className="text-[11px] text-slate-500">{dateTxt}</div>
                        </div>
                        <div className={`text-sm font-display font-semibold shrink-0 ${isCredit ? "text-emerald-700" : "text-rose-700"}`} data-testid={`kid-wallet-amount-${idx}`}>
                          {isCredit ? "+" : "-"}{formatGBP(Math.abs(delta) / 100)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )
            )}
          </div>
        </div>

        {kid.account_type === "organization" && (
          <div className="mt-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-teal-50 text-teal-800 border border-teal-200" data-testid="kid-ledger-cause-tag">
            Fundraising Cause Account
          </div>
        )}
        {kid.account_type === "organization" && (
          <CauseDashboard kidId={kid.id} />
        )}
        {kid.account_type === "organization" && (
          <PartnerAgreementCard kidId={kid.id} />
        )}
        <ChildLedger endpoint={`/kids/${id}/ledger`} title={`${kid.name}'s passbook`} testIdPrefix="kid-profile-ledger" />

        <div className="card-base p-6 md:p-8 mt-10" data-testid={activeFilter === "orders" ? "kid-orders" : "kid-listings"}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display text-2xl font-medium text-slate-900" data-testid="kid-listings-heading">
              {activeFilter === "orders" ? `${kid.name}'s orders` : (activeFilter ? `${kid.name}'s ${filterLabel.toLowerCase()}` : `${kid.name}'s listings`)}
            </h2>
            {activeFilter && (
              <button
                type="button"
                onClick={() => setActiveFilter(null)}
                className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-brandSlate px-2.5 py-1 rounded-full border border-slate-200 hover:border-brandSlate transition"
                data-testid="kid-clear-filter"
              >
                Show all
              </button>
            )}
          </div>
          {releaseFlash && (
            <div className="mt-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-900 flex items-center gap-2" data-testid="kid-listings-release-flash" role="status">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {releaseFlash}
            </div>
          )}

          {activeFilter === "orders" ? (
            /* Inline orders list — perspective-aware, matches the /orders
               page (iteration 109). Sellers see NET (green pill); buyers
               see GROSS (sky pill). No navigate away needed. */
            <div className="mt-5 space-y-2" data-testid="kid-orders-list">
              {ordersLoading ? (
                <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="kid-orders-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
                </div>
              ) : orders.length === 0 ? (
                <div className="col-span-full text-center py-10 text-slate-500" data-testid="kid-orders-empty">
                  No orders yet — sales and purchases will show up here.
                </div>
              ) : orders.map((o) => {
                const isSeller = o.role === "sold";
                const grossPence = Number(o.gross_pennies || o.total_pennies || o.amount_pennies || 0);
                const feePence = Number(o.fee_pennies || o.platform_fee_pennies || 0);
                const netRaw = Number(o.net_pennies || 0);
                let netPence = netRaw > 0 ? netRaw : Math.max(grossPence - feePence, 0);
                if (isSeller && netPence >= grossPence && grossPence > 0) {
                  // Canonical flat £0.30 platform fee fallback (iteration 109c).
                  netPence = Math.max(grossPence - 30, 0);
                }
                const displayPence = isSeller ? netPence : grossPence;
                const label = o.role_label
                  || (isSeller ? `Sold by ${o.seller_kid_name || kid.name}` : `Bought by ${o.buyer_kid_name || kid.name}`);
                const toneClass = isSeller
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-sky-100 text-sky-800 border-sky-200";
                return (
                  <div key={`${o.id}-${o.role}`} className="card-base p-3 flex items-center justify-between gap-3" data-testid={`kid-orders-row-${o.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${toneClass}`} data-testid={`kid-orders-tag-${o.id}`}>
                          {label}
                        </span>
                        <div className="font-semibold text-sm text-slate-900 truncate">{o.item_title || o.listing_title || o.title || "Order"}</div>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {(o.created_at || o.placed_at || "").slice(0, 10) || ""} · {o.status || "—"}
                      </div>
                    </div>
                    <div className="text-brandSlate font-semibold whitespace-nowrap" data-testid={`kid-orders-amount-${o.id}`}>
                      {formatGBP(displayPence / 100)}
                      {isSeller && <span className="ml-1 text-[10px] text-slate-500 font-semibold">net</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredListings.length === 0 ? (
                <div className="col-span-full text-center py-10 text-slate-500" data-testid="kid-listings-empty">
                  {activeFilter
                    ? `No ${filterLabel.toLowerCase()} yet.`
                    : "No listings yet."}
                </div>
              ) : filteredListings.map((l) => (
                <Link key={l.id} to={`/listing/${l.id}`} className="border border-slate-200 rounded-2xl overflow-hidden hover:-translate-y-0.5 hover:shadow-soft transition-all block" data-testid={`kid-listing-${l.id}`}>
                  <div className="aspect-square bg-slate-100 relative">
                    <ListingPhoto src={l.photos?.[0]} className="w-full h-full object-cover" alt="" />
                    {l.pay_what_you_can && (
                      <div className="absolute top-2 right-2">
                        <BestOfferBadge size="sm" testId={`kid-listing-best-offer-${l.id}`} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-display text-base font-medium text-slate-900 line-clamp-1">{l.title}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-blue-600 font-semibold">{formatGBP(l.price)}</div>
                      <StatusPill status={l.status} />
                    </div>
                    {/* Iteration 76 — canonical badge stack (condition, parcel, bulky, etc) */}
                    <ListingBadges
                      listing={l}
                      only={["new", "category", "condition", "parcel", "parent_posted"]}
                      className="mt-2"
                    />
                    {l.status === "reserved" && (
                      <button
                        type="button"
                        onClick={(e) => forceRelease(l.id, e)}
                        disabled={releasingId === l.id}
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-amber-300 hover:border-amber-500 text-amber-800 hover:bg-amber-50 text-xs font-bold transition disabled:opacity-60"
                        data-testid={`kid-listing-force-release-${l.id}`}
                        title="Reject the pending reservation and put this item back on the marketplace"
                      >
                        {releasingId === l.id
                          ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Releasing…</>)
                          : (<><Unlock className="w-3.5 h-3.5" /> Force Release</>)}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingListing(l); }}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-brandSlate text-slate-700 hover:bg-slate-50 text-xs font-bold transition"
                      data-testid={`kid-listing-edit-${l.id}`}
                      title="Edit this listing"
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
          </div>
        </div>
      </section>
      {editingListing && (
        <EditListingModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={(updated) => {
            setEditingListing(null);
            if (updated && updated.id) {
              setListings((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
            }
          }}
        />
      )}
      <Footer />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_parent: ["bg-amber-100 text-amber-800", "Pending"],
    live: ["bg-emerald-100 text-emerald-800", "Live"],
    reserved: ["bg-amber-100 text-amber-800", "Reserved"],
    sold: ["bg-blue-100 text-blue-800", "Sold"],
    rejected_ai: ["bg-rose-100 text-rose-800", "AI flagged"],
    rejected_parent: ["bg-rose-100 text-rose-800", "Rejected"],
  };
  const [cls, txt] = map[status] || ["bg-slate-100 text-slate-700", status || "—"];
  return <span className={`pill ${cls}`}>{txt}</span>;
}
